import React, { useState, useEffect } from 'react';
import { get } from '../../util/api';

const ImageModal = ({ image, onClose }) => {
	if (!image) return null;

	return (
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.8)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 2000,
				cursor: 'pointer',
			}}
			onClick={onClose}
		>
			<div
				style={{
					maxWidth: '90vw',
					maxHeight: '90vh',
					position: 'relative',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<img
					src={image}
					style={{
						maxWidth: '100%',
						maxHeight: '90vh',
						objectFit: 'contain',
					}}
					alt="Full size"
				/>
				<button
					onClick={onClose}
					style={{
						position: 'absolute',
						top: '-40px',
						right: 0,
						backgroundColor: 'transparent',
						border: 'none',
						color: 'white',
						fontSize: '24px',
						cursor: 'pointer',
						padding: '8px',
					}}
				>
					✕
				</button>
			</div>
		</div>
	);
};

const LazyImage = ({ src, alt, style, modifiedDate, onRefresh, showRefreshButton, isRefreshing, onImageClick }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [isInView, setIsInView] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);
	const imgRef = React.useRef();

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsInView(true);
					observer.disconnect();
				}
			},
			{
				rootMargin: '50px',
			}
		);

		if (imgRef.current) {
			observer.observe(imgRef.current);
		}

		return () => {
			if (imgRef.current) {
				observer.disconnect();
			}
		};
	}, []);

	const formatDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleString('tr-TR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	return (
		<div
			ref={imgRef}
			style={{ ...style, backgroundColor: '#f0f0f0', position: 'relative' }}
			onMouseEnter={() => setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}
		>
			{showRefreshButton && (
				<button
					onClick={(e) => {
						e.stopPropagation();
						onRefresh();
					}}
					disabled={isRefreshing}
					style={{
						position: 'absolute',
						top: '8px',
						right: '8px',
						backgroundColor: 'rgba(0, 0, 0, 0.6)',
						border: 'none',
						borderRadius: '4px',
						padding: '6px',
						cursor: isRefreshing ? 'not-allowed' : 'pointer',
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						opacity: isRefreshing ? 0.5 : 1,
					}}
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="white"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						style={{
							animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
						}}
					>
						<path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
					</svg>
				</button>
			)}

			{isInView && src && (
				<div onClick={onImageClick} style={{ cursor: 'pointer' }}>
					<img
						src={src}
						alt={alt}
						loading="lazy"
						style={{
							...style,
							opacity: isLoaded && !isRefreshing ? 1 : 0,
							transition: 'opacity 0.3s',
						}}
						onLoad={() => setIsLoaded(true)}
					/>
					{isRefreshing && (
						<div
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: '#f0f0f0',
							}}
						>
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="#666"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								style={{
									animation: 'spin 1s linear infinite',
								}}
							>
								<path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
							</svg>
						</div>
					)}
				</div>
			)}
			{!src && (
				<div
					style={{
						...style,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: '#f0f0f0',
						color: '#666',
					}}
				>
					No Image
				</div>
			)}
			{showTooltip && modifiedDate && (
				<div
					style={{
						position: 'absolute',
						bottom: '100%',
						left: '50%',
						transform: 'translateX(-50%)',
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						color: 'white',
						padding: '4px 8px',
						borderRadius: '4px',
						fontSize: '12px',
						whiteSpace: 'nowrap',
						zIndex: 1000,
					}}
				>
					{formatDate(modifiedDate)}
				</div>
			)}
			<style>
				{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
			</style>
		</div>
	);
};

const PhoneScreenGrid = () => {
	const [loading, setLoading] = useState(true);
	const [screens, setScreens] = useState([]);
	const [refreshingScreens, setRefreshingScreens] = useState(new Set());
	const [selectedImage, setSelectedImage] = useState(null);
	const [statusFilters, setStatusFilters] = useState({
		active: false,
		'qr-screen': true,
		loading: true,
		'conn-retry': true,
		TIMEOUT: true,
		PAIRING: true,
		'phone-error': true,
		'low-battery': true,
		idle: true,
		disabled: true,
		dispatch: true,
		'-': true,
		rocket: false, // Rocket filtresi, varsayılan olarak false
		hideRocket: false,
		hideFree: false,
		connectionError: true,
	});

	const refreshImage = async (screen) => {
		if (!screen.screen_url || refreshingScreens.has(screen.id)) return;

		try {
			setRefreshingScreens((prev) => new Set([...prev, screen.id]));

			await fetch(screen.screen_url);

			const newDate = new Date().toISOString();

			setScreens((currentScreens) =>
				currentScreens.map((s) => {
					if (s.id === screen.id) {
						return {
							...s,
							image_link: `${s.screen_url.split('?')[0]}?token=2yn9nWp_ue-t@dPxzPdXdbFB!U7!W466F&t=${Date.now()}`,
							modified_date: newDate,
						};
					}
					return s;
				})
			);
		} catch (error) {
			console.error('Error refreshing image:', error);
		} finally {
			setRefreshingScreens((prev) => {
				const newSet = new Set(prev);
				newSet.delete(screen.id);
				return newSet;
			});
		}
	};

	useEffect(() => {
		Promise.all([get('/maytapi/admin/listScreens'), get('/maytapi/admin/phoneListv2')])
			.then(([screensResponse, phonesResponse]) => {
				const phoneMap = phonesResponse.phones.reduce((acc, phone) => {
					acc[phone.id] = phone;
					return acc;
				}, {});

				// Group screens by base phone ID
				const screenGroups = screensResponse.result.reduce((acc, screen) => {
					const fullId = screen.image_link.split('/').pop().replace('.jpg', '');
					const baseId = fullId.replace('-connectionError', '');

					if (!acc[baseId]) {
						acc[baseId] = {
							normal: null,
							error: null,
						};
					}

					if (fullId.includes('-connectionError')) {
						acc[baseId].error = screen;
					} else {
						acc[baseId].normal = screen;
					}

					return acc;
				}, {});

				// Process screen groups into final screen data
				const screenData = Object.entries(screenGroups).flatMap(([baseId, images]) => {
					const phone = phoneMap[baseId];
					const isActive = phone?.status === 'active';

					const screens = [];
					// Always add the normal image if it exists
					if (images.normal) {
						screens.push({
							id: baseId,
							image_link: images.normal.image_link.replace('cdnydm.com', 'cdn.yapaytech.com') + `?t=${Date.now()}`,
							modified_date: images.normal.modified_date,
							status: (phone && phone.status) || '-',
							sessionId: (phone && phone.sessionId) || '-',
							pid: (phone && phone.pid) || '',
							screen_url: phone ? `https://api.maytapi.com/api/${phone.pid}/${baseId}/screen?token=2yn9nWp_ue-t@dPxzPdXdbFB!U7!W466F` : null,
						});
					}

					// Add the error image only if it exists AND the phone is not active
					if (images.error && !isActive) {
						screens.push({
							id: `${baseId}-connectionError`,
							image_link: images.error.image_link.replace('cdnydm.com', 'cdn.yapaytech.com') + `?t=${Date.now()}`,
							modified_date: images.error.modified_date,
							status: (phone && phone.status) || '-',
							sessionId: (phone && phone.sessionId) || '-',
							pid: (phone && phone.pid) || '',
							screen_url: phone ? `https://api.maytapi.com/api/${phone.pid}/${baseId}/screen?token=2yn9nWp_ue-t@dPxzPdXdbFB!U7!W466F` : null,
						});
					}

					return screens;
				});

				setScreens(screenData);
				setLoading(false);
			})
			.catch((error) => {
				console.error('Error fetching data:', error);
				setLoading(false);
			});
	}, []);

	if (loading) return <div>Loading...</div>;

	// Update the filtering logic to include the new sessionId filter
	const filteredScreens = screens.filter((screen) => {
		// Special handling for connectionError status
		if (statusFilters.connectionError) {
			if (screen.id.includes('-connectionError')) {
				return true;
			}
		}

		// Normal status kontrolü
		const statusMatch = statusFilters[screen.status];

		// Rocket kontrolü
		let rocketMatch = true;
		if (statusFilters.hideRocket) {
			rocketMatch = !screen.pid || screen.pid !== 'd9c31450-5a2b-4eef-a755-6534ce76e8f3';
		}

		// SessionId free kontrolü
		let sessionMatch = true;
		if (statusFilters.hideFree) {
			sessionMatch = screen.sessionId !== 'free';
		}

		// Return false for connectionError images when the filter is off
		if (!statusFilters.connectionError && screen.id.includes('-connectionError')) {
			return false;
		}

		return statusMatch && rocketMatch && sessionMatch;
	});

	return (
		<div className="w-full">
			<ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />

			<div
				style={{
					padding: '1rem',
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					position: 'sticky',
					top: 0,
					backgroundColor: 'white',
					zIndex: 1000,
				}}
			>
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters.active}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, active: e.target.checked }))}
						/>
						Active
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters['qr-screen']}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, 'qr-screen': e.target.checked }))}
						/>
						QR Screen
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters.loading}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, loading: e.target.checked }))}
						/>
						Loading
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters['phone-error']}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, 'phone-error': e.target.checked }))}
						/>
						Phone Error
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters.idle}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, idle: e.target.checked }))}
						/>
						Idle
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters.dispatch}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, dispatch: e.target.checked }))}
						/>
						Dispatch
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters.disabled}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, disabled: e.target.checked }))}
						/>
						Disabled
					</label>
					<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
						<input
							type="checkbox"
							checked={statusFilters.connectionError}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, connectionError: e.target.checked }))}
						/>
						Connection Error
					</label>
					<label
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '0.5rem',
							cursor: 'pointer',
							backgroundColor: 'rgba(0, 0, 0, 0.8)',
							color: 'white',
							padding: '0.25rem 0.5rem',
							borderRadius: '4px',
						}}
					>
						<input
							type="checkbox"
							checked={statusFilters.hideRocket}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, hideRocket: e.target.checked }))}
						/>
						Rocket Gizle
					</label>
					<label
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '0.5rem',
							cursor: 'pointer',
							backgroundColor: 'rgba(0, 0, 0, 0.8)',
							color: 'white',
							padding: '0.25rem 0.5rem',
							borderRadius: '4px',
						}}
					>
						<input
							type="checkbox"
							checked={statusFilters.hideFree}
							onChange={(e) => setStatusFilters((prev) => ({ ...prev, hideFree: e.target.checked }))}
						/>
						Sid Free Gizle
					</label>
				</div>
				<div style={{ marginLeft: 'auto' }}>
					Showing: {filteredScreens.length} / Total: {screens.length}
				</div>
			</div>
			<div style={{ padding: '1rem' }}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(6, 1fr)',
						gap: '1rem',
					}}
				>
					{filteredScreens.map((screen) => (
						<div key={screen.id} style={{ display: 'flex', flexDirection: 'column' }}>
							<LazyImage
								src={screen.image_link}
								alt={`Phone ${screen.id}`}
								style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }}
								modifiedDate={screen.modified_date}
								onRefresh={() => refreshImage(screen)}
								showRefreshButton={!!screen.screen_url}
								isRefreshing={refreshingScreens.has(screen.id)}
								onImageClick={() => setSelectedImage(screen.image_link)}
							/>

							<div
								style={{
									padding: '0.5rem',
									backgroundColor: 'rgba(0, 0, 0, 0.8)',
									color: 'white',
								}}
							>
								<div>ID: {screen.id}</div>
								<div>
									Status: {screen.status}, Sid: {screen.sessionId}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default PhoneScreenGrid;
